import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Chip, IconButton, Button, TextField, Paper, Grid,
  Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Alert, Select, MenuItem, FormControl, InputLabel,
  Stack, Divider, Tooltip, Checkbox, FormControlLabel, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery, useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PrintIcon from '@mui/icons-material/Print';
import {
  getPrescriptions, createPrescription, updatePrescription, deletePrescription,
  emitPrescription, searchDrugs, downloadPrescriptionPdf, logPatientMessage,
} from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useClinicFeatures } from '../../hooks/useClinicFeatures';
import { useNotification } from '../../hooks/useNotification';
import { tryParseJson, STATUS_RX, emptyRxItem, fmtDate, openWhatsApp } from './patientProfileHelpers';

const ItemRow = ({ item, idx, onUpdate, onRemove, canRemove }) => {
  const searchTimer = useRef(null);

  const handleInputChange = (val) => {
    onUpdate(idx, 'drug_name', val);
    onUpdate(idx, '_drugOptions', []);
    if (!val || val.length < 2) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await searchDrugs(val);
        onUpdate(idx, '_drugOptions', res.data || []);
      } catch {}
    }, 350);
  };

  const handleDrugSelect = (drug) => {
    if (!drug || typeof drug === 'string') return;
    const concs = tryParseJson(drug.concentrations, []);
    const forms = tryParseJson(drug.pharmaceutical_forms, []);
    onUpdate(idx, 'drug_name', drug.name);
    onUpdate(idx, '_selectedDrug', drug);
    onUpdate(idx, '_drugOptions', []);
    onUpdate(idx, 'concentration', concs[0] || '');
    onUpdate(idx, 'pharmaceutical_form', forms[0] || '');
  };

  const concs = item._selectedDrug ? tryParseJson(item._selectedDrug.concentrations, []) : [];
  const forms = item._selectedDrug ? tryParseJson(item._selectedDrug.pharmaceutical_forms, []) : [];

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Grid container spacing={1.5} alignItems="flex-start">
        {/* Drug name */}
        <Grid size={{ xs: 12, sm: 5 }}>
          <Autocomplete
            freeSolo
            options={item._drugOptions || []}
            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name)}
            inputValue={item.drug_name}
            onInputChange={(_, val, reason) => { if (reason === 'input') handleInputChange(val); }}
            onChange={(_, val) => { if (val && typeof val !== 'string') handleDrugSelect(val); }}
            filterOptions={(x) => x}
            size="small"
            renderOption={(props, opt) => {
              const forms = tryParseJson(opt.pharmaceutical_forms, []);
              return (
                <li {...props} key={opt.id}>
                  <Box>
                    <Typography variant="body2">{opt.name}</Typography>
                    {forms.length > 0 && (
                      <Typography variant="caption" color="text.secondary">{forms.join(' · ')}</Typography>
                    )}
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Medicamento *" placeholder="Buscar en vademecum…" />
            )}
          />
        </Grid>

        {/* Concentración */}
        <Grid size={{ xs: 6, sm: 2.5 }}>
          {concs.length > 1 ? (
            <FormControl fullWidth size="small">
              <InputLabel>Concentración</InputLabel>
              <Select value={item.concentration} label="Concentración"
                onChange={e => onUpdate(idx, 'concentration', e.target.value)}>
                {concs.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          ) : (
            <TextField label="Concentración" size="small" fullWidth
              value={item.concentration}
              onChange={e => onUpdate(idx, 'concentration', e.target.value)} />
          )}
        </Grid>

        {/* Forma farmacéutica */}
        <Grid size={{ xs: 6, sm: 2.5 }}>
          {forms.length > 1 ? (
            <FormControl fullWidth size="small">
              <InputLabel>Forma farm.</InputLabel>
              <Select value={item.pharmaceutical_form} label="Forma farm."
                onChange={e => onUpdate(idx, 'pharmaceutical_form', e.target.value)}>
                {forms.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </Select>
            </FormControl>
          ) : (
            <TextField label="Forma farm." size="small" fullWidth
              value={item.pharmaceutical_form}
              onChange={e => onUpdate(idx, 'pharmaceutical_form', e.target.value)} />
          )}
        </Grid>

        {/* Genérico + eliminar */}
        <Grid size={{ xs: 12, sm: 2 }} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox size="small" checked={item.is_generic}
                onChange={e => onUpdate(idx, 'is_generic', e.target.checked)} />
            }
            label={<Typography variant="caption">Genérico</Typography>}
            sx={{ m: 0 }}
          />
          {canRemove && (
            <IconButton size="small" color="error" onClick={onRemove} sx={{ ml: 'auto' }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Grid>

        {/* Cantidad */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField label="Cantidad" size="small" fullWidth
            value={item.quantity} placeholder="ej: 30 comprimidos"
            onChange={e => onUpdate(idx, 'quantity', e.target.value)} />
        </Grid>

        {/* Indicaciones */}
        <Grid size={{ xs: 12, sm: 8 }}>
          <TextField label="Indicaciones" size="small" fullWidth
            value={item.instructions} placeholder="ej: 1 comprimido cada 8 horas"
            onChange={e => onUpdate(idx, 'instructions', e.target.value)} />
        </Grid>
      </Grid>
    </Paper>
  );
};

// Lista de medicamentos de una receta expandida. En mobile una tabla de 6
// columnas (Medicamento/Conc./Forma/Cantidad/Indicaciones/Genérico) se
// comprimía ilegible — se reemplaza por una lista de líneas, mismo criterio
// que el resto de la app (Patients.js, Audit.js).
const MedicationList = ({ items, isMobile }) => {
  if (!isMobile) {
    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Medicamento</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Conc.</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Forma</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Cantidad</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Indicaciones</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem' }}>Genérico</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <TableRow key={item.id}>
              <TableCell><Typography variant="body2" fontWeight={600}>{item.drug_name}</Typography></TableCell>
              <TableCell><Typography variant="body2">{item.concentration || '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2">{item.pharmaceutical_form || '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2">{item.quantity || '—'}</Typography></TableCell>
              <TableCell><Typography variant="body2">{item.instructions || '—'}</Typography></TableCell>
              <TableCell>
                <Chip label={item.is_generic ? 'Sí' : 'No'} size="small"
                  color={item.is_generic ? 'success' : 'default'} variant="outlined" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }
  return (
    <Stack spacing={1}>
      {items.map(item => (
        <Box key={item.id} sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none', pb: 0 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>
              {item.drug_name}{item.concentration ? ` ${item.concentration}` : ''}
            </Typography>
            <Chip label={item.is_generic ? 'Genérico' : 'Marca'} size="small"
              color={item.is_generic ? 'success' : 'default'} variant="outlined"
              sx={{ height: 18, fontSize: '0.62rem' }} />
          </Box>
          {item.pharmaceutical_form && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.pharmaceutical_form}</Typography>
          )}
          {item.quantity && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Cantidad: {item.quantity}</Typography>
          )}
          {item.instructions && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{item.instructions}</Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
};

// ── Tab: Recetas electrónicas ─────────────────────────────────────────────────
export const RecetasTab = ({ patient, doctors = [] }) => {
  const { isAdmin, isMedico, doctorId } = useAuth();
  const { features } = useClinicFeatures();
  const notify = useNotification();
  const canWrite = isAdmin || isMedico;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [expanded, setExpanded]           = useState(null);

  // Form state
  const [formOpen, setFormOpen]         = useState(false);
  const [editingRx, setEditingRx]       = useState(null);
  const [formDoctorId, setFormDoctorId] = useState('');
  const [formDiagnosis, setFormDiagnosis] = useState('');
  const [formNotes, setFormNotes]       = useState('');
  const [formItems, setFormItems]       = useState([emptyRxItem()]);
  const [formError, setFormError]       = useState('');
  const [saving, setSaving]             = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rxRes = await getPrescriptions({ patient_id: patient.id, limit: 100 });
      setPrescriptions(rxRes.data || []);
    } catch {}
    finally { setLoading(false); }
  }, [patient.id]);

  useEffect(() => { load(); }, [load]);

  const updateItem = useCallback((idx, field, value) =>
    setFormItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it)),
  []);

  const openCreate = () => {
    setEditingRx(null);
    setFormDoctorId(isMedico && doctorId ? String(doctorId) : '');
    setFormDiagnosis(''); setFormNotes('');
    setFormItems([emptyRxItem()]);
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (rx) => {
    setEditingRx(rx);
    setFormDoctorId(String(rx.doctor_id));
    setFormDiagnosis(rx.diagnosis_hint || '');
    setFormNotes(rx.notes || '');
    setFormItems(
      rx.items.length > 0
        ? rx.items.map(i => ({
            drug_name: i.drug_name, concentration: i.concentration || '',
            pharmaceutical_form: i.pharmaceutical_form || '',
            quantity: i.quantity || '', instructions: i.instructions || '',
            is_generic: i.is_generic, _drugOptions: [], _selectedDrug: null,
          }))
        : [emptyRxItem()]
    );
    setFormError('');
    setFormOpen(true);
  };

  const handleSave = async (andEmit = false) => {
    if (!formDoctorId) { setFormError('Seleccioná un médico'); return; }
    const validItems = formItems.filter(i => i.drug_name.trim());
    if (validItems.length === 0) { setFormError('Agregá al menos un medicamento'); return; }
    const payload = validItems.map(({ drug_name, concentration, pharmaceutical_form, quantity, instructions, is_generic }) =>
      ({ drug_name, concentration, pharmaceutical_form, quantity, instructions, is_generic })
    );

    setSaving(true); setFormError('');
    let rxId = editingRx?.id;
    try {
      if (editingRx) {
        await updatePrescription(editingRx.id, { diagnosis_hint: formDiagnosis, notes: formNotes, items: payload });
      } else {
        const res = await createPrescription({
          patient_id: patient.id,
          doctor_id: Number(formDoctorId),
          diagnosis_hint: formDiagnosis,
          notes: formNotes,
          items: payload,
        });
        rxId = res.data.id;
        // La receta borrador ya quedó creada en el servidor — si la emisión
        // de abajo falla, hay que pasar a modo "editar" sobre ESTA receta en
        // vez de dejar el diálogo en modo "crear": un reintento del usuario
        // creaba una segunda receta borrador duplicada en vez de reintentar
        // sobre la que ya existe.
        setEditingRx({ id: rxId });
      }
      if (andEmit) await emitPrescription(rxId);
      setFormOpen(false);
      await load();
    } catch (err) {
      if (andEmit && rxId) {
        setFormError((err.userMessage || 'No se pudo emitir la receta') + ' — la receta quedó guardada como borrador, podés reintentar emitirla.');
      } else {
        setFormError(err.userMessage || 'Error al guardar');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEmit = async (rx) => {
    if (!window.confirm('¿Emitir esta receta? No podrá editarse después.')) return;
    try { await emitPrescription(rx.id); await load(); }
    catch (err) { notify(err.userMessage || 'No se pudo emitir la receta', 'error'); }
  };

  const handleDelete = async (rx) => {
    if (!window.confirm('¿Eliminar esta receta?')) return;
    try { await deletePrescription(rx.id); await load(); }
    catch (err) { notify(err.userMessage || 'No se pudo eliminar la receta', 'error'); }
  };

  const handleWhatsAppRx = async (rx) => {
    if (!patient.phone) {
      notify('El paciente no tiene número de teléfono registrado', 'warning');
      return;
    }
    const verifyUrl = `${window.location.origin}/verify?token=${encodeURIComponent(rx.signature)}`;
    const items = rx.items.map(i => `• ${i.drug_name}${i.concentration ? ` ${i.concentration}` : ''}${i.instructions ? ` — ${i.instructions}` : ''}`).join('\n');
    const text = `Hola ${patient.first_name || patient.name}, te envío tu receta digital del ${fmtDate(rx.created_at)}.\n\n${items}\n\nVerificá tu receta en: ${verifyUrl}`;
    openWhatsApp(patient.phone, text);
    try {
      await logPatientMessage(patient.id, {
        channel: 'whatsapp',
        message_type: 'receta',
        message_preview: `Receta del ${fmtDate(rx.created_at)} — ${rx.items.map(i => i.drug_name).join(', ')}`,
      });
      notify('WhatsApp abierto', 'success');
    } catch {
      // El mensaje ya se abrió en WhatsApp (irreversible) — solo avisamos
      // que quedó afuera del historial de comunicaciones, no lo tratamos
      // como error del envío en sí.
      notify('WhatsApp abierto, pero no se pudo registrar en el historial de comunicaciones', 'warning');
    }
  };

  const handlePdf = async (rx) => {
    try {
      const res = await downloadPrescriptionPdf(rx.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      notify(err.userMessage || 'No se pudo generar el PDF', 'error');
    }
  };

  if (!features.prescriptions) {
    return (
      <Box sx={{ mt: 4, textAlign: 'center', py: 6 }}>
        <Typography color="text.secondary" gutterBottom>
          El módulo de recetas electrónicas no está habilitado.
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Activalo desde Configuración → Módulos.
        </Typography>
      </Box>
    );
  }

  const doctorById = (id) => doctors.find(d => d.id === id);
  const selectedDoctor = formDoctorId ? doctorById(Number(formDoctorId)) : null;
  const canEmitForm    = selectedDoctor?.matricula && formItems.some(i => i.drug_name.trim());

  return (
    <Box sx={{ mt: 2 }}>
      {canWrite && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Nueva receta
          </Button>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : prescriptions.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
          Sin recetas registradas para este paciente.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {prescriptions.map(rx => {
            const st       = STATUS_RX[rx.status] || STATUS_RX.borrador;
            const doc      = doctorById(rx.doctor_id);
            const isOpen   = expanded === rx.id;
            const isBorr   = rx.status === 'borrador';
            return (
              <Paper key={rx.id} variant="outlined" sx={{ overflow: 'hidden' }}>
                {/* Header — flexWrap: en mobile, chip+fecha+médico+diagnóstico+hasta
                    4 IconButton de acción no entran en una sola fila de
                    ~360px; sin wrap se apretaban o desbordaban. El bloque de
                    acciones pasa a su propia fila cuando no entra. */}
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, rowGap: 0.5, flexWrap: 'wrap', px: 2, py: 1.5, cursor: 'pointer' }}
                  onClick={() => setExpanded(isOpen ? null : rx.id)}
                >
                  <Chip label={st.label} size="small"
                    sx={{ bgcolor: st.color, color: 'white', fontWeight: 700, minWidth: 74, flexShrink: 0 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                    {fmtDate(rx.created_at)}
                  </Typography>
                  {doc && (
                    <Typography variant="body2" color="text.secondary" noWrap
                      sx={{ flexShrink: 1, minWidth: 0, maxWidth: 160 }}>
                      Dr. {doc.name}
                    </Typography>
                  )}
                  {rx.diagnosis_hint && (
                    <Typography variant="body2" color="text.secondary"
                      sx={{ flex: 1, minWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {rx.diagnosis_hint}
                    </Typography>
                  )}
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Typography variant="caption" color="text.disabled">{rx.items.length} med.</Typography>
                    {canWrite && isBorr && (
                      <>
                        <Tooltip title="Editar">
                          <IconButton size="small"
                            onClick={e => { e.stopPropagation(); openEdit(rx); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Emitir receta">
                          <IconButton size="small" color="success"
                            onClick={e => { e.stopPropagation(); handleEmit(rx); }}>
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error"
                            onClick={e => { e.stopPropagation(); handleDelete(rx); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {(rx.status === 'emitida' || rx.status === 'vencida') && (
                      <>
                        {features?.whatsapp && (
                          <Tooltip title="Enviar receta por WhatsApp">
                            <IconButton size="small" sx={{ color: '#25D366' }}
                              onClick={e => { e.stopPropagation(); handleWhatsAppRx(rx); }}>
                              <WhatsAppIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Imprimir / descargar PDF">
                          <IconButton size="small"
                            onClick={e => { e.stopPropagation(); handlePdf(rx); }}>
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <ExpandMoreIcon sx={{
                      fontSize: 18, color: 'text.disabled',
                      transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none',
                    }} />
                  </Box>
                </Box>

                {/* Items expandidos */}
                {isOpen && (
                  <Box sx={{ px: 2, pb: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    {rx.notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                        <strong>Notas:</strong> {rx.notes}
                      </Typography>
                    )}
                    <MedicationList items={rx.items} isMobile={isMobile} />
                  </Box>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Dialog crear / editar */}
      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRx ? 'Editar receta' : 'Nueva receta'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small" disabled={isMedico}>
                <InputLabel>Médico *</InputLabel>
                <Select value={formDoctorId} label="Médico *"
                  onChange={e => setFormDoctorId(e.target.value)}>
                  {doctors.map(d => (
                    <MenuItem key={d.id} value={String(d.id)}>
                      Dr. {d.name}{d.matricula ? ` — Mat. ${d.matricula}` : ' ⚠ sin matrícula'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedDoctor && !selectedDoctor.matricula && (
                <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
                  Sin matrícula — no se podrá emitir hasta que la cargue.
                </Alert>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Diagnóstico / Indicación" fullWidth size="small"
                value={formDiagnosis} onChange={e => setFormDiagnosis(e.target.value)} />
            </Grid>
            <Grid size={12}>
              <TextField label="Notas adicionales" fullWidth size="small" multiline rows={2}
                value={formNotes} onChange={e => setFormNotes(e.target.value)} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
            Medicamentos
          </Typography>

          {formItems.map((item, idx) => (
            <ItemRow
              key={idx}
              item={item}
              idx={idx}
              onUpdate={updateItem}
              onRemove={() => setFormItems(prev => prev.filter((_, i) => i !== idx))}
              canRemove={formItems.length > 1}
            />
          ))}

          <Button startIcon={<AddIcon />} size="small"
            onClick={() => setFormItems(prev => [...prev, emptyRxItem()])}>
            Agregar medicamento
          </Button>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={() => handleSave(false)} disabled={saving}>
            {saving ? <CircularProgress size={16} /> : 'Guardar borrador'}
          </Button>
          <Tooltip title={!selectedDoctor?.matricula ? 'El médico no tiene matrícula cargada' : ''}>
            <span>
              <Button
                variant="contained"
                color="success"
                startIcon={<SendIcon />}
                onClick={() => handleSave(true)}
                disabled={saving || !canEmitForm}
              >
                {saving ? <CircularProgress size={16} /> : 'Guardar y emitir'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
